import React from 'react';
import { Database } from 'lucide-react';
import { AVAILABLE_BIDDERS, AVAILABLE_USER_ID_MODULES } from '../config/prebidOptions';

const EnhancedUserIdSection = ({ formData, setFormData, config }) => {
  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4">
        <Database className="h-5 w-5 text-blue-600" />
        <h3 className="text-lg font-medium text-gray-900">User ID Modules</h3>
        <div className="ml-auto">
          <span className="text-xs text-gray-500">
            Configure which bidders receive each user ID
          </span>
        </div>
      </div>
      
      <div className="space-y-4">
        {Object.entries(AVAILABLE_USER_ID_MODULES).map(([moduleId, moduleConfig]) => {
          const currentConfig = formData.userIdModules[moduleId] || {};
          const isEnabled = currentConfig.enabled ?? false;
          const enabledBidders = Object.keys(config?.bidders || {}).filter(bidder => 
            config?.bidders[bidder]?.enabled
          );
          
          return (
            <div key={moduleId} className="border border-gray-200 rounded-md p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      
                      setFormData(prev => ({
                        ...prev,
                        userIdModules: {
                          ...prev.userIdModules,
                          [moduleId]: {
                            // Merge with existing config or default config
                            ...(currentConfig.enabled !== undefined ? currentConfig : moduleConfig.defaultConfig),
                            enabled: isChecked
                          }
                        }
                      }));
                    }}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div>
                    <label className="text-sm font-medium text-gray-700">
                      {moduleConfig.label}
                    </label>
                    <p className="text-xs text-gray-500">{moduleConfig.description}</p>
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${
                  isEnabled 
                    ? 'bg-green-100 text-green-800' 
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {isEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              
              {isEnabled && (
                <div className="ml-7 space-y-4 pt-3 border-t border-gray-100">
                  
                  {/* Bidder Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Target Bidders
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {enabledBidders.map(bidderName => {
                        const bidderConfig = AVAILABLE_BIDDERS[bidderName];
                        const isSelected = currentConfig.bidders?.includes(bidderName);
                        const isPreferred = moduleConfig.preferredBidders?.includes(bidderName);
                        const isSupported = moduleConfig.supportedBidders?.includes(bidderName);
                        
                        return (
                          <label key={bidderName} className={`flex items-center gap-2 p-2 rounded border cursor-pointer ${
                            isSelected ? 'bg-blue-50 border-blue-200' : 'border-gray-200 hover:bg-gray-50'
                          } ${!isSupported ? 'opacity-50 cursor-not-allowed' : ''}`}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              disabled={!isSupported}
                              onChange={(e) => {
                                const updatedBidders = e.target.checked
                                  ? [...(currentConfig.bidders || []), bidderName]
                                  : (currentConfig.bidders || []).filter(b => b !== bidderName);
                                
                                setFormData(prev => ({
                                  ...prev,
                                  userIdModules: {
                                    ...prev.userIdModules,
                                    [moduleId]: {
                                      ...currentConfig,
                                      bidders: updatedBidders
                                    }
                                  }
                                }));
                              }}
                              className="h-3 w-3 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <span className={`text-xs ${isPreferred ? 'font-medium text-blue-700' : 'text-gray-600'}`}>
                              {bidderConfig?.label || bidderName}
                              {isPreferred && ' ★'}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      ★ = Preferred bidder for this ID • Unchecked bidders won't receive this ID
                    </p>
                  </div>

                  {/* Storage Configuration */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Storage Type
                      </label>
                      <select
                        value={currentConfig.storage?.type || 'html5'}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            userIdModules: {
                              ...prev.userIdModules,
                              [moduleId]: {
                                ...currentConfig,
                                storage: {
                                  ...currentConfig.storage,
                                  type: e.target.value
                                }
                              }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {moduleConfig.storageTypes?.map(type => (
                          <option key={type} value={type}>
                            {type === 'html5' ? 'HTML5 LocalStorage' : 
                             type === 'cookie' ? 'Cookie' : 
                             type === 'both' ? 'Both' : type}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Storage Name
                      </label>
                      <input
                        type="text"
                        value={currentConfig.storage?.name || moduleConfig.defaultConfig?.storage?.name || ''}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            userIdModules: {
                              ...prev.userIdModules,
                              [moduleId]: {
                                ...currentConfig,
                                storage: {
                                  ...currentConfig.storage,
                                  name: e.target.value
                                }
                              }
                            }
                          }));
                        }}
                        placeholder="Custom storage key name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Module-specific and universal settings */}
                  <div className="grid grid-cols-2 gap-4">
                    {moduleId === 'teadsId' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Publisher ID
                        </label>
                        <input
                          type="number"
                          value={currentConfig.pubId || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              userIdModules: {
                                ...prev.userIdModules,
                                [moduleId]: {
                                  ...currentConfig,
                                  pubId: parseInt(e.target.value) || null
                                }
                              }
                            }));
                          }}
                          placeholder="Enter Teads Publisher ID"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    {moduleId === 'id5Id' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Partner ID
                        </label>
                        <input
                          type="number"
                          value={currentConfig.partner || ''}
                          onChange={(e) => {
                            setFormData(prev => ({
                              ...prev,
                              userIdModules: {
                                ...prev.userIdModules,
                                [moduleId]: {
                                  ...currentConfig,
                                  partner: parseInt(e.target.value) || null
                                }
                              }
                            }));
                          }}
                          placeholder="Enter ID5 Partner ID"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Expiry (days)
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="365"
                        value={currentConfig.expiry || 30}
                        onChange={(e) => {
                          setFormData(prev => ({
                            ...prev,
                            userIdModules: {
                              ...prev.userIdModules,
                              [moduleId]: {
                                ...currentConfig,
                                expiry: parseInt(e.target.value) || 30
                              }
                            }
                          }));
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  {/* Advanced Storage Options */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refresh Interval (seconds) - Optional
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={currentConfig.storage?.refreshInSeconds || ''}
                      onChange={(e) => {
                        const value = e.target.value === '' ? null : parseInt(e.target.value);
                        setFormData(prev => ({
                          ...prev,
                          userIdModules: {
                            ...prev.userIdModules,
                            [moduleId]: {
                              ...currentConfig,
                              storage: {
                                ...currentConfig.storage,
                                refreshInSeconds: value
                              }
                            }
                          }
                        }));
                      }}
                      placeholder="Auto-refresh interval (leave empty for no refresh)"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      How often to refresh the user ID. Leave empty for no automatic refresh.
                    </p>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default EnhancedUserIdSection;